import { useState } from 'react';
import Cross from '../../icons/Cross'
import Loading from '../../icons/Loading';
import { BsFillCreditCard2FrontFill } from 'react-icons/bs';
import CardNumberValidate from '../../components/CardNumberValidate';
import CvvValidate from '../../components/CvvValidate';
import ExpiryDateValidator from '../../components/ExpiryDateValidator';
import { INVOICE_TYPES, InvoiceType } from './Billing';

type UpgradePlanProps = {
    showPopupForPayment: boolean;
    setShowPopupForPayment: (value: boolean) => void;
    invoiceType: InvoiceType;
    upgradPlan: any;
    rechargeAmount: string;
    isProcessing: boolean;
    setIsProcessing: (value: boolean) => void;
    callTheAPIForUpgradeSubscription: (upgradedPlan: any, paymentDetails: any, cb: any) => void;
    callTheAPIToRechargeTheWallet: (amount: number, paymentDetails: any, cb: any) => void;
    paymentType: number;
    setIsRefresh: (value: boolean) => void;
}

const UpgradePlan = ({
    showPopupForPayment,
    setShowPopupForPayment,
    invoiceType,
    upgradPlan,
    rechargeAmount,
    isProcessing,
    setIsProcessing,
    callTheAPIForUpgradeSubscription,
    callTheAPIToRechargeTheWallet,
    paymentType,
    setIsRefresh
}: UpgradePlanProps) => {
    const [creditCardDetails, setCreditCardDetails] = useState<any>({
        cardholderName: "",
        creditCardNumber: "",
        expirationDate: "",
        ccvCode: "",
        ccLastFourDigit: ""
    });

    const [achDetails, setACHDetails] = useState<any>({
        accountNumber: "",
        accountType: "",
        routingNumber: "",
        bankName: "",
        bankPhone: "",
        accountHolderName: "",
        useAccountInfoInFuture: false,
        signedAcknowledgement: false
    });

    const closeModal = () => setShowPopupForPayment(false);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!isProcessing) {
            setIsProcessing(true);

            const paymentDetails = paymentType === 0 ? creditCardDetails : achDetails;

            if (invoiceType === INVOICE_TYPES.Subscription) {
                callTheAPIForUpgradeSubscription(upgradPlan, paymentDetails, resetTheFrom);
            } else {
                const amount = Number(rechargeAmount);
                callTheAPIToRechargeTheWallet(amount, paymentDetails, resetTheFrom);
            }
        }
    }

    const resetTheFrom = () => {
        if (paymentType === 0) {
            setCreditCardDetails({
                cardholderName: "",
                creditCardNumber: "",
                expirationDate: "",
                ccvCode: ""
            });
        } else {
            setACHDetails({
                accountNumber: "",
                accountType: "",
                routingNumber: "",
                bankName: "",
                bankPhone: "",
                accountHolderName: "",
                useAccountInfoInFuture: false,
                signedAcknowledgement: false
            });
        }

        setIsRefresh(true);
        closeModal();
    }

    return (
        <div className={`fixed inset-0 p-4 mt-14 ${showPopupForPayment ? "flex" : "hidden"} flex-wrap justify-center items-center w-full h-fit z-[1000] before:fixed before:inset-0 before:w-full before:min-h-[calc(100vh-72px)] before:bg-[rgba(0,0,0,0.5)] overflow-auto`}>
            <div className="w-full max-w-lg bg-white shadow-lg rounded-md relative">

                <div className="text-primary bg-secondary px-4 py-2 w-full flex justify-between items-center rounded-tl-md rounded-tr-md">
                    <h4 className="font-medium">Payment</h4>

                    <Cross
                        className="w-3 cursor-pointer shrink-0 fill-white active:fill-secondary float-right"
                        closeModal={closeModal}
                    />
                </div>

                <hr className="w-full bg-secondary/20 h-0.5" />

                <div className="w-full px-4 pt-4 pb-8">
                    <form className="mt-2 space-y-4" onSubmit={handleSubmit}>
                        {
                            paymentType === 0
                                ? (
                                    <>
                                        <div className="flex flex-col">
                                            <label className="text-sm mb-1">Cardholder Name</label>
                                            <input
                                                name="cardholderName"
                                                type="text"
                                                className="bg-transparent w-full text-sm rounded-md border border-secondary pl-2 pr-[40px] py-3 outline-none placeholder:text-[#bbb]"
                                                placeholder="Enter Cardholder Name"
                                                value={creditCardDetails.cardholderName}
                                                onChange={(e) => setCreditCardDetails({ ...creditCardDetails, cardholderName: e.target.value })}
                                                required />
                                        </div>

                                        <div className="relative flex flex-col">
                                            <CardNumberValidate
                                                value={creditCardDetails.creditCardNumber}
                                                setValue={(value) =>
                                                    setCreditCardDetails({ ...creditCardDetails, creditCardNumber: value })
                                                }
                                            />

                                            <BsFillCreditCard2FrontFill className="w-[18px] h-[18px] absolute right-4 items-center top-2/3 transform -translate-y-1/2 text-[#bbbbbb]" />
                                        </div>

                                        <div className="flex justify-between items-center gap-x-4">
                                            <div className="relative flex-1 flex flex-col">
                                                <ExpiryDateValidator
                                                    expiryDate={creditCardDetails.expirationDate}
                                                    setExpiryDate={(value) =>
                                                        setCreditCardDetails({ ...creditCardDetails, expirationDate: value })
                                                    }
                                                />
                                            </div>

                                            <div className="relative flex-1 flex flex-col">
                                                <CvvValidate
                                                    value={creditCardDetails.ccvCode}
                                                    setValue={(value) =>
                                                        setCreditCardDetails({ ...creditCardDetails, ccvCode: value })
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="flex flex-col">
                                            <label className="text-sm mb-1">
                                                Account Holder Name
                                            </label>
                                            <input
                                                type="text"
                                                className="bg-transparent w-full text-sm rounded-md border border-secondary pl-2 pr-[40px] py-3 outline-none placeholder:text-[#bbb]"
                                                value={achDetails.accountHolderName}
                                                onChange={(e) =>
                                                    setACHDetails({ ...achDetails, accountHolderName: e.target.value })
                                                }
                                                required
                                            />
                                        </div>

                                        <div className="flex flex-col">
                                            <label className="text-sm mb-1">
                                                Account Number
                                            </label>
                                            <input
                                                type="text"
                                                className="bg-transparent w-full text-sm rounded-md border border-secondary pl-2 pr-[40px] py-3 outline-none placeholder:text-[#bbb]"
                                                value={achDetails.accountNumber}
                                                onChange={(e) =>
                                                    setACHDetails({ ...achDetails, accountNumber: e.target.value })
                                                }
                                                required
                                            />
                                        </div>

                                        <div className="flex flex-col">
                                            <label className="text-sm mb-1">
                                                Routing Number
                                            </label>
                                            <input
                                                type="text"
                                                className="bg-transparent w-full text-sm rounded-md border border-secondary pl-2 pr-[40px] py-3 outline-none placeholder:text-[#bbb]"
                                                value={achDetails.routingNumber}
                                                onChange={(e) =>
                                                    setACHDetails({ ...achDetails, routingNumber: e.target.value })
                                                }
                                                required
                                            />
                                        </div>

                                        <div className="flex flex-col">
                                            <label className="text-sm mb-1">
                                                Bank Name
                                            </label>
                                            <input
                                                type="text"
                                                className="bg-transparent w-full text-sm rounded-md border border-secondary pl-2 pr-[40px] py-3 outline-none placeholder:text-[#bbb]"
                                                value={achDetails.bankName}
                                                onChange={(e) =>
                                                    setACHDetails({ ...achDetails, bankName: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="flex flex-col">
                                            <label className="text-sm mb-1">
                                                Bank Phone
                                            </label>
                                            <input
                                                type="text"
                                                className="bg-transparent w-full text-sm rounded-md border border-secondary pl-2 pr-[40px] py-3 outline-none placeholder:text-[#bbb]"
                                                value={achDetails.bankPhone}
                                                onChange={(e) =>
                                                    setACHDetails({ ...achDetails, bankPhone: e.target.value })
                                                }
                                            />
                                        </div>

                                        <div className="flex flex-col">
                                            <label className="text-sm mb-1">
                                                Account Type
                                            </label>
                                            <select
                                                className="bg-transparent w-full text-sm rounded-md border border-secondary pl-2 pr-[40px] py-3 outline-none placeholder:text-[#bbb]"
                                                value={achDetails.accountType}
                                                onChange={(e) =>
                                                    setACHDetails({ ...achDetails, accountType: e.target.value })
                                                }
                                                required
                                            >
                                                <option value="">Select</option>
                                                <option value="checking">Checking</option>
                                                <option value="savings">Savings</option>
                                            </select>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="useAccountInfoInFuture"
                                                checked={achDetails.useAccountInfoInFuture}
                                                onChange={(e) =>
                                                    setACHDetails({
                                                        ...achDetails,
                                                        useAccountInfoInFuture: e.target.checked
                                                    })
                                                }
                                            />
                                            <label htmlFor="useAccountInfoInFuture" className={`text-xs`}>
                                                Use account info in future
                                            </label>
                                        </div>

                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id="signedAcknowledgement"
                                                checked={achDetails.signedAcknowledgement}
                                                onChange={(e) =>
                                                    setACHDetails({
                                                        ...achDetails,
                                                        signedAcknowledgement: e.target.checked
                                                    })
                                                }
                                                required
                                            />
                                            <label htmlFor="signedAcknowledgement" className={`text-xs`}>
                                                I acknowledge and authorize ACH transactions
                                            </label>
                                        </div>
                                    </>
                                )
                        }

                        <div className="pt-4">
                            <button
                                type='submit'
                                className="text-white bg-secondary rounded-md border border-secondary px-2 py-2 w-full text-center"
                            >
                                {isProcessing ?
                                    <>
                                        <Loading />
                                        Processing...
                                    </>
                                    :
                                    "Pay"
                                }
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div >
    )
}

export default UpgradePlan;